/**
 * Maps Connector Renderer payloads to BATON zone rows for discovery.
 */

import type { Renderer } from '../lib/connector';
import { classifyRendererZoneType } from './rendererZoneType';

export { classifyRendererZoneType, resolveLogRendererType, isSystemOutputLabel, isLocalSystemOutputZone } from './rendererZoneType';

export interface Zone {
  id: string;
  name: string;
  type: 'upnp' | 'airplay' | 'chromecast' | 'browser' | 'manual' | 'lua' | 'psaudio' | 'local-native';
  host: string;
  port: number;
  status: 'available' | 'connected' | 'playing' | 'offline';
  capabilities: {
    protocols: string[];
    streaming?: boolean;
    volume?: boolean;
    fixedVolume?: boolean; volumeNotControllable?: boolean; // volume set on the player itself (Connector self-check)
  };
  lastSeen: number;
  metadata?: Record<string, unknown>;
}

/** Streaming is not inferred from raat_port alone when can_play is false. */
export function rendererZoneStreamingCapable(r: Renderer): boolean {
  if (r.can_play === false) return false;
  if (r.can_play === true) return true;
  return !r.raat_port;
}

export function rendererToZone(r: Renderer): Zone {
  const transport = r.transport_type || 'upnp';
  const zoneType = classifyRendererZoneType(r);

  let port = 0;
  try {
    if (r.control_url) {
      const parsed = new URL(r.control_url);
      if (parsed.port) port = parseInt(parsed.port, 10);
    }
  } catch { /* invalid URL */ }

  const pickerSelectable = r.picker_selectable !== false;

  return {
    id: r.id,
    name: r.custom_name || r.name,
    type: zoneType,
    host: r.ip || '',
    port,
    status: 'available',
    capabilities: {
      protocols: r.sink_protocols || [],
      streaming: rendererZoneStreamingCapable(r),
      volume: !!(r.volume_url || r.raat_port || r.transport_type === 'qobuz-connect'), // Qobuz Connect carries volume
      fixedVolume: !!r.fixed_volume || !!r.volume_not_controllable, volumeNotControllable: !!r.volume_not_controllable,
    },
    lastSeen: Date.now(),
    metadata: {
      source: 'native',
      model: r.model,
      manufacturer: r.manufacturer,
      transport_type: transport,
      is_roon_ready: r.is_roon_ready || false,
      icon_url: r.icon_url || '',
      uses_stop_for_pause: r.uses_stop_for_pause || false,
      control_url: r.control_url || '',
      volume_url: r.volume_url || '',
      raat_port: r.raat_port || null,
      resync_delay_ms: r.resync_delay_ms ?? 0,
      gapless_enabled: r.gapless_enabled !== false,
      avt_supports_set_next: r.avt_supports_set_next === true,
      audio_device_id: r.audio_device_id || '',
      picker_selectable: pickerSelectable,
      picker_disabled_reason: pickerSelectable ? null : (r.picker_disabled_reason || null),
    },
  };
}

export function makeLocalComputerZone(): Zone {
  return {
    id: 'local_computer',
    name: 'System Audio',
    type: 'browser',
    host: '127.0.0.1',
    port: 0,
    status: 'available',
    capabilities: { protocols: ['*'], streaming: true, volume: true },
    lastSeen: Date.now(),
  };
}

export function renderersToZones(renderers: Renderer[]): Zone[] {
  return [makeLocalComputerZone(), ...renderers.map(rendererToZone)];
}

export function isZonePickerSelectable(zone: Zone): boolean {
  return zone.metadata?.picker_selectable !== false;
}

export function zonePickerDisabledReason(zone: Zone): string | null {
  if (isZonePickerSelectable(zone)) return null;
  const reason = zone.metadata?.picker_disabled_reason;
  return typeof reason === 'string' && reason.length > 0 ? reason : null;
}
